import React, { FC, useEffect } from 'react';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { Button } from 'src/presentation/components/agent_desktop_components';
import chatCampaignSettingsResource from 'src/actions/chatCampaigns/chatCampaignSettingsResource';
import Form, { FormEditorProps, useForm, useStandardRepoOnSave } from 'src/presentation/components/forms/Form';
import { FormSection } from 'src/presentation/components/forms/Layout';
import { FooterLarge } from 'src/presentation/components/forms/Footer';
import InputField, { InputType } from 'src/presentation/components/forms/InputField';
import { InputFieldContainer } from 'src/presentation/components/pages/shared/EditorSectionLayout';
import RadioGroup from 'src/presentation/components/forms/RadioGroup';
import { Row } from 'src/presentation/components/forms/Layout';
import { StyledBody } from 'src/presentation/components/forms/Body';

const defaultItem = {
  id: 'chat_campaign_settings',
  unlimitedCampaignsPerSession: true,
  maxCampaignsPerSession: 0,
  version: 1,
};

const isStateModified = (prevState, currentState) => {
  const unlimitedCampaignsPerSessionChanged =
    prevState.unlimitedCampaignsPerSession !== currentState.unlimitedCampaignsPerSession;
  const maxCampaignsPerSessionChanged =
    currentState.unlimitedCampaignsPerSession === false &&
    prevState.maxCampaignsPerSession !== currentState.maxCampaignsPerSession;
  return maxCampaignsPerSessionChanged || unlimitedCampaignsPerSessionChanged;
};

const ChatCampaignsSettingModalForm: FC<FormEditorProps & { onClose: () => void }> = ({ item, errors, onClose }) => {
  const dispatch = useDispatch();
  const resetErrors = chatCampaignSettingsResource.actions.resetErrors as any;
  const saveHandler = useStandardRepoOnSave({ updateSuccessToastMessage: 'Updated Proactive Chat settings' });
  const formContext = useForm({
    defaultItem,
    item,
    errors,
    resetErrors,
    root: 'proactive-chat-settings',
    onSave: saveHandler,
    update: chatCampaignSettingsResource.effects.replace,
    validate: chatCampaignSettingsResource.effects.validate,
  });

  const { onSave, updateState, overrideState, workingState } = formContext;

  useEffect(() => {
    if (!isEqual(item.version, workingState.version)) {
      overrideState(item);
    }
  }, [item, overrideState, workingState]);

  const unlimitedCampaignsPerSession = get(workingState, 'unlimitedCampaignsPerSession');

  useEffect(() => {
    const maxCampaignsPerSession = get(workingState, 'maxCampaignsPerSession', '');
    // this is require because BE is not accept empty value for maxCampaignsPerSession
    if (unlimitedCampaignsPerSession && maxCampaignsPerSession === '') {
      updateState(set({}, 'maxCampaignsPerSession', 0));
      dispatch(resetErrors());
    }
  }, [workingState, updateState, unlimitedCampaignsPerSession, dispatch, resetErrors]);

  const hasChanged = isStateModified(item, workingState);

  const handleSave = () => {
    onSave();
    onClose();
  };

  return (
    <Form context={formContext}>
      <StyledBody data-aid={'editor'}>
        <FormSection>
          <RadioGroup
            options={[
              { label: 'Unlimited', value: true },
              { label: 'Limited', value: false },
            ]}
            data-aid="unlimitedCampaignsPerSession"
            attr="unlimitedCampaignsPerSession"
            label="Set the maximum number of Proactive Chat Campaigns a customer can trigger during their visit."
          />
        </FormSection>
        <InputRow isShown={!unlimitedCampaignsPerSession}>
          <InputFieldContainer>
            <InputField type={InputType.number} attr="maxCampaignsPerSession" data-aid="maxCampaignsPerSession" />
          </InputFieldContainer>
        </InputRow>
      </StyledBody>
      <FooterLarge>
        <Button type={Button.Types.TEXT} data-aid="modalCancel" onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid={'editor-save'} onClick={handleSave} disabled={!hasChanged}>
          Save
        </Button>
      </FooterLarge>
    </Form>
  );
};

const InputRow = styled(Row)`
  margin-left: 18px;
  margin-bottom: 0;
  visibility: ${props => (props.isShown ? 'visible' : 'hidden')};
`;

export default ChatCampaignsSettingModalForm;
